import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateTransactionRequest,
  Transaction,
  TransactionQuery,
  UpdateTransactionRequest,
} from '@financial/shared';
import { api } from '../../lib/api';
import { notify } from '../../store/notifications';

function keyFor(query: TransactionQuery) {
  return ['transactions', query];
}

async function listTransactions(query: TransactionQuery): Promise<Transaction[]> {
  const res = await api.get<Transaction[]>('/transactions', { params: query });
  return res.data;
}

export function useTransactions(query: TransactionQuery = {}) {
  return useQuery({ queryKey: keyFor(query), queryFn: () => listTransactions(query) });
}

export function useTransaction(id: string | null) {
  return useQuery({
    queryKey: ['transactions', 'detail', id],
    queryFn: async () => (await api.get<Transaction>(`/transactions/${id}`)).data,
    enabled: Boolean(id),
    retry: false,
  });
}

function useInvalidateTx() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['transactions'] });
    void qc.invalidateQueries({ queryKey: ['accounts'] });
    void qc.invalidateQueries({ queryKey: ['net-worth'] });
    void qc.invalidateQueries({ queryKey: ['insights'] });
    void qc.invalidateQueries({ queryKey: ['budget'] });
  };
}

export function useCreateTransaction() {
  const invalidate = useInvalidateTx();
  return useMutation({
    mutationFn: async (body: CreateTransactionRequest) => {
      const res = await api.post<Transaction>('/transactions', body);
      return res.data;
    },
    onSuccess: (transaction, variables) => {
      invalidate();
      if (variables.reconciliation) {
        notify.success('Movimiento registrado y saldo conciliado');
        return;
      }
      notify.success('Movimiento registrado', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await api.delete(`/transactions/${transaction.id}`);
            invalidate();
            notify.info('Movimiento deshecho');
          },
        },
      });
    },
    // Si la respuesta se perdió o el saldo ya había cambiado, consulta el
    // estado canónico antes de que la persona intente conciliar de nuevo.
    onError: (_error, variables) => {
      if (variables.reconciliation) invalidate();
    },
  });
}

export function useDeleteTransaction() {
  const invalidate = useInvalidateTx();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/transactions/${id}`);
    },
    onSuccess: invalidate,
  });
}

export function useUpdateTransaction() {
  const invalidate = useInvalidateTx();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: UpdateTransactionRequest;
      successMessage?: string;
    }) => {
      const res = await api.patch<Transaction>(`/transactions/${id}`, body);
      return res.data;
    },
    onSuccess: (_transaction, variables) => {
      invalidate();
      notify.success(variables.successMessage ?? 'Movimiento actualizado');
    },
  });
}
